import Modal from 'Common/Components/Modal';
import ButtonBase from 'components/ui/buttons/ButtonBase';
import React, { FC, useEffect } from 'react';
import { TextField } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import { updateShipmentAmountThunk, getShipmentsThunk } from '@redux/features/shipment.slice';

type IProps = {
    show: boolean;
    handleClose: () => void;
    shipment: {
        id: number;
        balance: number;
        lastCost: number;
    } | null;
};

const UpdateShipmentModal: FC<IProps> = ({ show, handleClose, shipment }) => {
    const dispatch = useAppDispatch();
    const { updateShipmentAmountLoading } = useAppSelector((state: RootState) => state.shipmentState);

    const validationSchema = Yup.object({
        balance: Yup.number()
            .required('Balance is required')
            .min(0, 'Balance must be greater than or equal to 0'),
        lastCost: Yup.number()
            .required('Last Cost is required')
            .min(0, 'Last Cost must be greater than or equal to 0'),
    });

    const formik = useFormik({
        initialValues: {
            balance: shipment?.balance || 0,
            lastCost: shipment?.lastCost || 0,
        },
        validationSchema,
        enableReinitialize: true,
        onSubmit: (values) => {
            dispatch(
                updateShipmentAmountThunk({
                    id: 1, // Fixed ID as requested
                    balance: values.balance,
                    lastCost: values.lastCost,
                })
            ).then(() => {
                dispatch(getShipmentsThunk());
                handleClose();
            });
        },
    });

    useEffect(() => {
        if (shipment) {
            formik.setValues({
                balance: shipment.balance,
                lastCost: shipment.lastCost,
            });
        }
    }, [shipment]);

    return (
        <Modal
            show={show}
            onHide={handleClose}
            dialogClassName="w-full bg-white p-3 shadow rounded-md overflow-scroll md:w-[100%] md:max-w-[500px]"
            backdropClassName="backdrop-overlay fixed inset-0 z-[1051] bg-slate-900/70 dark:bg-zink-900/85"
        >
            <Modal.Header>
                <Modal.Title className="text-lg font-semibold">Update Shipment</Modal.Title>
            </Modal.Header>
            <Modal.Body className="my-5">
                <form onSubmit={formik.handleSubmit} className="flex flex-col gap-4">
                    <TextField
                        size="small"
                        fullWidth
                        id="balance"
                        name="balance"
                        label="Balance"
                        type="number"
                        value={formik.values.balance}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.balance && Boolean(formik.errors.balance)}
                        helperText={formik.touched.balance && formik.errors.balance}
                        disabled={updateShipmentAmountLoading}
                        inputProps={{ step: '0.01' }}
                    />
                    <TextField
                        size="small"
                        fullWidth
                        id="lastCost"
                        name="lastCost"
                        label="Last Cost"
                        type="number"
                        value={formik.values.lastCost}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.lastCost && Boolean(formik.errors.lastCost)}
                        helperText={formik.touched.lastCost && formik.errors.lastCost}
                        disabled={updateShipmentAmountLoading}
                        inputProps={{ step: '0.01' }}
                    />
                </form>
            </Modal.Body>
            <Modal.Footer className="flex items-center justify-center gap-2 mt-5">
                <ButtonBase
                    innerText="Cancel"
                    className="bg-gray-500 hover:bg-gray-600 rounded border-none px-4 py-2 text-white"
                    onClick={handleClose}
                    disabled={updateShipmentAmountLoading}
                />
                <ButtonBase
                    innerText="Update"
                    className="bg-yellow-600 hover:bg-yellow-800 rounded border-none px-4 py-2 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={() => formik.handleSubmit()}
                    disabled={updateShipmentAmountLoading}
                />
            </Modal.Footer>
        </Modal>
    );
};

export default UpdateShipmentModal;

